import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { useTranslation } from '../../hooks/useTranslation';
import { ClientOnlyUserProfile } from '../Auth/UserProfile';
import { cn } from '../../utils/classNames';
import { LocalizedLink } from '../LocalizedLink';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface NavItem {
  name: string;
  link: string;
  isExternal?: boolean;
}

interface NavbarProps {
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export const Navbar: React.FC<NavbarProps> = ({ containerRef }) => {
  const { t } = useTranslation();
  const navRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { scrollY } = useScroll({
    target: containerRef || navRef,
    offset: ["start start", "end start"],
  });

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  });

  const navItems: NavItem[] = [
    {
      name: t('navigation.features'),
      link: "/features",
    },
    {
      name: t('navigation.pricing'),
      link: "/pricing",
    },
    {
      name: t('navigation.contact'),
      link: "/contact",
    },
  ];

  return (
    <motion.div ref={navRef} className="w-full fixed top-0 inset-x-0 z-[70]">
      {/* Desktop Nav */}
      <motion.div
        onMouseLeave={() => {
          setHovered(null);
        }}
        animate={{
          backdropFilter: visible ? "blur(10px)" : "none",
          boxShadow: visible
            ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
            : "none",
          width: visible ? "40%" : "100%",
          y: visible ? 20 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 50,
        }}
        style={{
          minWidth: "800px",
        }}
        className={cn(
          "hidden lg:flex flex-row self-start bg-transparent dark:bg-transparent items-center justify-between py-2 max-w-7xl mx-auto pl-4 pr-2 rounded-full relative z-[60] w-full",
          visible && "bg-white/80 dark:bg-neutral-950/80"
        )}
      >
        {/* Logo */}
        <div className="flex items-end gap-3 z-[61] relative">
          <LocalizedLink
            to="/"
            className="z-[61] relative cursor-pointer"
          >
            <h1 className="logo-font text-xl ml-2 text-gray-800 dark:text-white hover:text-primary-600/90 transition-all duration-300 transform hover:scale-[1.01] select-none font-light">
              {t('landing.headerTitle')}
            </h1>
          </LocalizedLink>
        </div>

        {/* Nav Items */}
        <motion.div className="lg:flex flex-row flex-1 absolute inset-0 hidden items-center justify-center space-x-2 lg:space-x-2 text-sm text-zinc-600 font-medium hover:text-zinc-800 transition duration-200">
          {navItems.map((navItem, idx) => (
            navItem.link.startsWith('#') ? (
              <a
                onMouseEnter={() => setHovered(idx)}
                className="text-neutral-600 dark:text-neutral-300 relative px-4 py-2"
                key={`link=${idx}`}
                href={navItem.link}
              >
                {hovered === idx && (
                  <motion.div
                    layoutId="hovered"
                    className="w-full h-full absolute inset-0 bg-gray-100 dark:bg-neutral-800 rounded-full"
                  />
                )}
                <span className="relative z-20">{navItem.name}</span>
              </a>
            ) : (
              <LocalizedLink
                onMouseEnter={() => setHovered(idx)}
                className="text-neutral-600 dark:text-neutral-300 relative px-4 py-2"
                key={`link=${idx}`}
                to={navItem.link}
              >
                {hovered === idx && (
                  <motion.div
                    layoutId="hovered"
                    className="w-full h-full absolute inset-0 bg-gray-100 dark:bg-neutral-800 rounded-full"
                  />
                )}
                <span className="relative z-20">{navItem.name}</span>
              </LocalizedLink>
            )
          ))}
        </motion.div>

        {/* User Profile */}
        <div className="flex items-center gap-4 z-[61] relative">
          <LanguageSwitcher compact={visible} />
          <ClientOnlyUserProfile />
        </div>
      </motion.div>

      {/* Mobile Nav */}
      <motion.div
        animate={{
          backdropFilter: visible ? "blur(10px)" : "none",
          boxShadow: visible
            ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
            : "none",
          width: visible ? "90%" : "100%",
          y: visible ? 20 : 0,
          borderRadius: mobileMenuOpen ? "4px" : "2rem",
          paddingRight: visible ? "12px" : "0px",
          paddingLeft: visible ? "12px" : "0px",
        }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 50,
        }}
        className={cn(
          "flex relative flex-col lg:hidden w-full justify-between items-center bg-transparent max-w-[calc(100vw-2rem)] mx-auto px-0 py-2 z-50",
          visible && "bg-white/80 dark:bg-neutral-950/80"
        )}
      >
        <div className="flex flex-row justify-between items-center w-full px-4">
          <LocalizedLink
            to="/"
            className="z-[51] relative cursor-pointer"
          >
            <h1 className="logo-font text-2xl text-gray-800 dark:text-white hover:text-primary-600/90 transition-all duration-300 transform hover:scale-[1.01] select-none font-light">
              {t('landing.headerTitle')}
            </h1>
          </LocalizedLink>
          {mobileMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-black dark:text-white cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-black dark:text-white cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex rounded-lg absolute top-16 bg-white dark:bg-neutral-950 inset-x-0 z-50 flex-col items-start justify-start gap-4 w-full px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]"
            >
              {navItems.map((navItem, idx) => (
                navItem.link.startsWith('#') ? (
                  <a
                    key={`link=${idx}`}
                    href={navItem.link}
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative text-neutral-600 dark:text-neutral-300"
                  >
                    <motion.span className="block">{navItem.name} </motion.span>
                  </a>
                ) : (
                  <LocalizedLink
                    key={`link=${idx}`}
                    to={navItem.link}
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative text-neutral-600 dark:text-neutral-300"
                  >
                    <motion.span className="block">{navItem.name} </motion.span>
                  </LocalizedLink>
                )
              ))}
              <div className="w-full pt-4">
                <LanguageSwitcher className="mb-4" compact={visible} />
                <ClientOnlyUserProfile />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}; 